import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Grid,
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  InputLabel,
  FormHelperText,
  InputAdornment,
  Chip
} from '@mui/material';
import {
  Home as HomeIcon,
  Store as StoreIcon,
  ShoppingCart as ShoppingCartIcon
} from '@mui/icons-material';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';
import { createOrder, getShippingCompanies } from '../../services/storeService';
import { createOrderBostaDelivery, calculateShippingRate, getBostaCities, getBostaCityZones } from '../../services/bostaService';
import { getCustomerAddresses, addAddress } from '../../services/addressService';
import { Order, PaymentMethod, PaymentStatus, OrderStatus, ShippingCompany, ShippingRegion, CustomerAddress } from '../../types/store';
import { BostaCity, BostaZone, BostaRateResponse } from '../../types/bosta';
import { DEFAULT_EGYPT_SHIPPING_REGIONS } from '../../services/storeSettingsService';
// استخدام مسار نسبي للصورة الافتراضية
const placeholderImage = '/assets/images/product-placeholder.png';

const steps = ['معلومات الشحن', 'طريقة الدفع', 'مراجعة الطلب'];

interface ShippingFormData {
  fullName: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  region: string;
  notes: string;
  shippingMethod: string;
  // Bosta fields
  bostaCityId?: string;
  bostaCityName?: string;
  bostaZoneId?: string;
  bostaZoneName?: string;
  bostaDistrictId?: string;
  bostaDistrictName?: string;
  building?: string;
  floor?: string;
  apartment?: string;
}

interface PaymentFormData {
  paymentMethod: PaymentMethod;
}

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { cart, clearCart } = useCart();
  const { currentUser } = useAuth();
  const { settings } = useStoreSettings();
  const [activeStep, setActiveStep] = useState(0);
  const [shippingData, setShippingData] = useState<ShippingFormData>({
    fullName: currentUser?.name || '',
    phone: '',
    email: currentUser?.email || '',
    address: '',
    city: '',
    region: '',
    notes: '',
    shippingMethod: ''
  });
  const [paymentData, setPaymentData] = useState<PaymentFormData>({
    paymentMethod: PaymentMethod.CASH_ON_DELIVERY
  });
  const [shippingCompanies, setShippingCompanies] = useState<ShippingCompany[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<ShippingRegion | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [bostaCities, setBostaCities] = useState<BostaCity[]>([]);
  const [bostaZones, setBostaZones] = useState<BostaZone[]>([]);
  const [bostaLoading, setBostaLoading] = useState(false);
  const [bostaRate, setBostaRate] = useState<BostaRateResponse | null>(null);
  const [bostaLoadingRate, setBostaLoadingRate] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState<CustomerAddress[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string>('');
  const [useNewAddress, setUseNewAddress] = useState(true);

  const availablePaymentMethods = React.useMemo(() => {
    const paymentSettings = settings?.payment;
    const methods: PaymentMethod[] = [];

    if (!paymentSettings || paymentSettings.enableCashOnDelivery) {
      methods.push(PaymentMethod.CASH_ON_DELIVERY);
    }

    if (!paymentSettings || paymentSettings.enableBankTransfer) {
      methods.push(PaymentMethod.BANK_TRANSFER);
    }

    if (paymentSettings?.enableElectronicPayment) {
      methods.push(PaymentMethod.ELECTRONIC_PAYMENT);
    }

    return methods;
  }, [settings?.payment]);

  const getPaymentMethodLabel = (method: PaymentMethod) => {
    switch (method) {
      case PaymentMethod.CASH_ON_DELIVERY:
        return 'الدفع عند الاستلام';
      case PaymentMethod.BANK_TRANSFER:
        return 'تحويل بنكي';
      case PaymentMethod.ELECTRONIC_PAYMENT:
        return 'دفع إلكتروني';
      default:
        return method;
    }
  };

  useEffect(() => {
    if (availablePaymentMethods.length === 0) return;

    if (!availablePaymentMethods.includes(paymentData.paymentMethod)) {
      setPaymentData({ paymentMethod: availablePaymentMethods[0] });
    }
  }, [availablePaymentMethods, paymentData.paymentMethod]);

  // جلب المدن المتاحة لطريقة الشحن المحددة أو الافتراضية
  const getAvailableCities = useCallback(() => {
    if (shippingData.shippingMethod === 'bosta') {
      return [];
    }

    const company = shippingData.shippingMethod
      ? shippingCompanies.find(c => c.id === shippingData.shippingMethod)
      : null;

    const list: { id: string; name: string; cost: number; days: number; regionId: string; regionName: string }[] = [];

    if (company) {
      company.regions.forEach(region => {
        if (!region.isActive) return;

        if (region.cities && region.cities.length > 0) {
          region.cities.forEach(city => {
            if (!city.isActive) return;
            list.push({
              id: city.id,
              name: city.name,
              cost: city.shippingCost,
              days: city.estimatedDeliveryDays,
              regionId: region.id,
              regionName: region.name
            });
          });
        } else {
          list.push({
            id: region.id,
            name: region.name,
            cost: region.cost,
            days: region.estimatedDeliveryDays,
            regionId: region.id,
            regionName: region.name
          });
        }
      });
    } else {
      // استخدام المناطق الافتراضية كاحتياطي
      DEFAULT_EGYPT_SHIPPING_REGIONS.forEach(region => {
        if (!region.isActive) return;
        if (region.cities && region.cities.length > 0) {
          region.cities.forEach(city => {
            if (!city.isActive) return;
            list.push({
              id: city.id,
              name: city.name,
              cost: city.shippingCost,
              days: city.estimatedDeliveryDays,
              regionId: region.id,
              regionName: region.name
            });
          });
        } else {
          list.push({
            id: region.id,
            name: region.name,
            cost: 40,
            days: 3,
            regionId: region.id,
            regionName: region.name
          });
        }
      });
    }

    // ترتيب المدن أبجدياً
    return list.sort((a, b) => a.name.localeCompare(b.name, 'ar'));
  }, [shippingData.shippingMethod, shippingCompanies]);

  // معالجة اختيار المدينة من القائمة المنسدلة
  const handleCityDropdownChange = (cityName: string) => {
    const citiesList = getAvailableCities();
    const city = citiesList.find(c => c.name === cityName);

    setShippingData(prev => ({
      ...prev,
      city: cityName,
      region: city ? city.regionId : ''
    }));

    if (city) {
      setSelectedRegion({
        id: city.regionId,
        name: city.regionName,
        cost: city.cost,
        estimatedDeliveryDays: city.days,
        isActive: true
      });
    } else {
      setSelectedRegion(null);
    }

    // إزالة رسائل الخطأ
    if (formErrors.city) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.city;
        return newErrors;
      });
    }
    if (formErrors.region) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.region;
        return newErrors;
      });
    }
  };

  useEffect(() => {
    // التحقق من وجود منتجات في السلة
    if (cart.items.length === 0 && !orderComplete) {
      navigate('/');
    }

    // جلب شركات الشحن
    const fetchShippingCompanies = async () => {
      try {
        console.log('Fetching shipping companies in CheckoutPage...');
        const companies = await getShippingCompanies();
        console.log('Shipping companies received:', companies);

        // طباعة تفاصيل شركات الشحن للتصحيح
        if (companies && companies.length > 0) {
          companies.forEach(company => {
            console.log(`Company: ${company.name}, ID: ${company.id}`);
            console.log(`Regions: ${company.regions ? company.regions.length : 0}`);
            if (company.regions && company.regions.length > 0) {
              company.regions.forEach(region => {
                console.log(`  Region: ${region.name}, ID: ${region.id}, Cost: ${region.cost}`);
              });
            } else {
              console.warn(`  No regions found for company: ${company.name}`);
            }
          });
        }

        setShippingCompanies(companies);

        // إذا لم تكن هناك شركات شحن، عرض رسالة خطأ
        if (!companies || companies.length === 0) {
          console.warn('No shipping companies available');
          setError('لا توجد طرق شحن متاحة. يرجى التواصل مع إدارة المتجر.');
        } else if (companies.some(company => !company.regions || company.regions.length === 0)) {
          console.warn('Some shipping companies have no regions');
          setError('بعض شركات الشحن ليس لديها مناطق شحن. يرجى التواصل مع إدارة المتجر.');
        } else {
          setError(null);
        }
      } catch (err) {
        console.error('Error fetching shipping companies:', err);
        setError('حدث خطأ أثناء تحميل خيارات الشحن');
      }
    };

    fetchShippingCompanies();

    // تحميل العناوين المحفوظة
    if (currentUser?.id) {
      getCustomerAddresses(currentUser.id)
        .then(addresses => {
          setSavedAddresses(addresses);
          // اختيار العنوان الافتراضي تلقائياً أو أول عنوان متوفر
          const defaultAddr = addresses.find(a => a.isDefault) || addresses[0];
          if (defaultAddr) {
            selectAddress(defaultAddr);
          }
        })
        .catch(() => {});
    }
  }, [cart.items.length, navigate, orderComplete, currentUser]);

  // تحديث المنطقة المختارة عند تغيير طريقة الشحن
  useEffect(() => {
    if (shippingData.shippingMethod) {
      const company = shippingCompanies.find(c => c.id === shippingData.shippingMethod);
      if (company && company.regions.length > 0 && shippingData.region) {
        const region = company.regions.find(r => r.id === shippingData.region);
        setSelectedRegion(region || null);
      } else {
        setSelectedRegion(null);
      }
    }
  }, [shippingData.shippingMethod, shippingData.region, shippingCompanies]);

  // اختيار عنوان محفوظ
  const selectAddress = (address: CustomerAddress) => {
    setSelectedAddressId(address.id);
    setUseNewAddress(false);

    setShippingData(prev => ({
      ...prev,
      fullName: address.fullName,
      phone: address.phone,
      email: address.email || prev.email,
      address: address.address,
      city: address.city,
      region: address.region,
      building: address.building || '',
      floor: address.floor || '',
      apartment: address.apartment || '',
      bostaCityId: address.bostaCityId || '',
      bostaCityName: address.bostaCityName || '',
      bostaZoneId: address.bostaZoneId || '',
      bostaZoneName: address.bostaZoneName || '',
      bostaDistrictId: address.bostaDistrictId || '',
      bostaDistrictName: address.bostaDistrictName || '',
      shippingMethod: address.defaultShippingMethod || prev.shippingMethod
    }));
    // تطبيق تفضيلات الدفع
    if (address.defaultPaymentMethod) {
      setPaymentData({ paymentMethod: address.defaultPaymentMethod as PaymentMethod });
    }
  };

  // اختيار عنوان جديد
  const selectNewAddress = () => {
    setSelectedAddressId('');
    setUseNewAddress(true);
    setShippingData(prev => ({
      ...prev,
      fullName: currentUser?.name || '',
      phone: '',
      email: currentUser?.email || '',
      address: '',
      city: '',
      region: '',
      building: '',
      floor: '',
      apartment: '',
      bostaCityId: '',
      bostaCityName: '',
      bostaZoneId: '',
      bostaZoneName: '',
      bostaDistrictId: '',
      bostaDistrictName: ''
    }));
  };

  // تحميل مدن Bosta عند اختيار Bosta كشركة شحن
  useEffect(() => {
    if (shippingData.shippingMethod === 'bosta') {
      setBostaLoading(true);
      getBostaCities()
        .then(cities => {
          setBostaCities(cities || []);
        })
        .catch(err => console.error('Error loading Bosta cities:', err))
        .finally(() => setBostaLoading(false));
    } else {
      setBostaCities([]);
    }
  }, [shippingData.shippingMethod]);

  // تحميل مناطق Bosta عند اختيار مدينة
  useEffect(() => {
    if (shippingData.bostaCityId) {
      getBostaCityZones(shippingData.bostaCityId)
        .then(zones => setBostaZones(zones || []))
        .catch(() => setBostaZones([]));
    } else {
      setBostaZones([]);
    }
  }, [shippingData.bostaCityId]);

  // حساب تكلفة الشحن التلقائي من Bosta
  useEffect(() => {
    if (shippingData.shippingMethod === 'bosta' && shippingData.bostaCityId && shippingData.bostaDistrictName) {
      const selectedCity = bostaCities.find(c => c._id === shippingData.bostaCityId);
      if (!selectedCity) return;

      setBostaLoadingRate(true);
      calculateShippingRate({
        dropOffCity: selectedCity.name,
        dropOffZone: shippingData.bostaZoneName || '',
        dropOffDistrict: shippingData.bostaDistrictName,
        packageType: 'SMALL',
        itemsCount: 1
      })
        .then(rate => {
          setBostaRate(rate);
        })
        .catch(err => {
          console.error('Error calculating Bosta rate:', err);
          setBostaRate(null);
        })
        .finally(() => setBostaLoadingRate(false));
    } else {
      setBostaRate(null);
    }
  }, [shippingData.shippingMethod, shippingData.bostaCityId, shippingData.bostaDistrictName, shippingData.bostaZoneName]);

  const validateShippingForm = () => {
    const errors: Record<string, string> = {};

    if (!shippingData.fullName.trim()) {
      errors.fullName = 'الاسم الكامل مطلوب';
    }

    if (!shippingData.phone.trim()) {
      errors.phone = 'رقم الهاتف مطلوب';
    } else if (!/^\d{10,11}$/.test(shippingData.phone.trim())) {
      errors.phone = 'رقم الهاتف غير صحيح';
    }

    if (!shippingData.email.trim()) {
      errors.email = 'البريد الإلكتروني مطلوب';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shippingData.email.trim())) {
      errors.email = 'البريد الإلكتروني غير صحيح';
    }

    if (!shippingData.address.trim()) {
      errors.address = 'العنوان مطلوب';
    }

    if (!shippingData.shippingMethod) {
      errors.shippingMethod = 'يرجى اختيار طريقة الشحن';
    }

    if (shippingData.shippingMethod === 'bosta') {
      if (!shippingData.bostaCityId) {
        errors.region = 'يرجى اختيار المدينة';
      }
      if (!shippingData.bostaDistrictName) {
        errors.region = errors.region || 'يرجى كتابة اسم الحي';
      }
    } else if (!shippingData.city.trim()) {
      errors.city = 'يرجى اختيار المدينة';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (activeStep === 0) {
      // التحقق من صحة نموذج الشحن
      if (!validateShippingForm()) {
        return;
      }
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleShippingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setShippingData((prev) => ({ ...prev, [name]: value }));

    // إزالة رسالة الخطأ عند تغيير القيمة
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleShippingMethodChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;

    // البحث عن الشركة وتحديث المنطقة وسعر الشحن تلقائياً للمدينة المحددة
    const company = shippingCompanies.find(c => c.id === value);
    let updatedRegion: ShippingRegion | null = null;
    let newRegionId = '';

    if (company && company.regions && shippingData.city) {
      let foundCity: any = null;
      company.regions.forEach(region => {
        if (!region.isActive) return;
        const city = region.cities?.find(c => c.name === shippingData.city && c.isActive);
        if (city) {
          foundCity = {
            id: city.id,
            name: city.name,
            cost: city.shippingCost,
            days: city.estimatedDeliveryDays,
            regionId: region.id,
            regionName: region.name
          };
        } else if (region.name === shippingData.city) {
          foundCity = {
            id: region.id,
            name: region.name,
            cost: region.cost,
            days: region.estimatedDeliveryDays,
            regionId: region.id,
            regionName: region.name
          };
        }
      });

      if (foundCity) {
        newRegionId = foundCity.regionId;
        updatedRegion = {
          id: foundCity.regionId,
          name: foundCity.regionName,
          cost: foundCity.cost,
          estimatedDeliveryDays: foundCity.days,
          isActive: true
        };
      }
    }

    setShippingData((prev) => ({
      ...prev,
      shippingMethod: value,
      region: newRegionId
    }));

    setSelectedRegion(updatedRegion);

    // إزالة رسالة الخطأ
    if (formErrors.shippingMethod) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.shippingMethod;
        return newErrors;
      });
    }
  };

  const handleRegionChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    setShippingData((prev) => ({ ...prev, region: value }));

    // إزالة رسالة الخطأ
    if (formErrors.region) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.region;
        return newErrors;
      });
    }

    // تحديث المنطقة المختارة
    if (shippingData.shippingMethod) {
      const company = shippingCompanies.find(c => c.id === shippingData.shippingMethod);
      if (company) {
        const region = company.regions.find(r => r.id === value);
        setSelectedRegion(region || null);
      }
    }
  };

  const handlePaymentMethodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as PaymentMethod;
    setPaymentData((prev) => ({ ...prev, paymentMethod: value }));
  };

  const handlePlaceOrder = async () => {
    if (cart.items.length === 0) {
      setError('سلة التسوق فارغة');
      return;
    }

    if (!availablePaymentMethods.includes(paymentData.paymentMethod)) {
      setError('طريقة الدفع المختارة غير متاحة حالياً. يرجى اختيار طريقة دفع مفعلة من إعدادات المتجر.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // إنشاء كائن الطلب
      const isBostaShipping = shippingData.shippingMethod === 'bosta';
      const isPickup = shippingData.shippingMethod === 'pickup';
      const shippingCost = isPickup ? 0 : (isBostaShipping ? (bostaRate?.total || 0) : (selectedRegion?.cost || 0));

      const orderData: Omit<Order, 'id' | 'createdAt' | 'updatedAt'> = {
        orderNumber: '', // سيتم تعيينه تلقائيًا بعد إنشاء الطلب
        customerId: currentUser?.id || 'guest',
        customerName: shippingData.fullName,
        customerPhone: shippingData.phone,
        customerEmail: shippingData.email,
        items: cart.items.map(item => ({
          productId: item.productId,
          productName: item.product.name,
          productSku: item.product.sku,
          quantity: item.quantity,
          price: item.product.sellingPrice,
          total: item.product.sellingPrice * item.quantity,
          warehouseId: item.product.warehouseId || ''
        })),
        subtotal: cart.totalAmount,
        shippingCost,
        discount: 0,
        tax: 0,
        total: cart.totalAmount + shippingCost,
        paymentMethod: paymentData.paymentMethod,
        paymentStatus: PaymentStatus.PENDING,
        orderStatus: OrderStatus.PENDING,
        shippingDetails: {
          address: shippingData.address,
          city: isBostaShipping ? (shippingData.bostaCityName || '') : shippingData.city,
          region: isBostaShipping ? (shippingData.bostaDistrictName || shippingData.bostaDistrictId || '') : shippingData.region,
          phone: shippingData.phone,
          shippingMethod: shippingData.shippingMethod,
          shippingCost,
          firstName: shippingData.fullName.split(' ')[0] || shippingData.fullName,
          lastName: shippingData.fullName.split(' ').slice(1).join(' ') || '',
          street: shippingData.address,
          building: shippingData.building || '',
          floor: shippingData.floor || '',
          apartment: shippingData.apartment || '',
          bostaCityName: shippingData.bostaCityName || '',
          bostaCityId: shippingData.bostaCityId || '',
          bostaZoneName: shippingData.bostaZoneName || '',
          bostaZoneId: shippingData.bostaZoneId || '',
          bostaDistrict: shippingData.bostaDistrictName || '',
          bostaDistrictId: shippingData.bostaDistrictId || ''
        },
        notes: shippingData.notes
      };

      // إرسال الطلب إلى قاعدة البيانات
      const createdOrder = await createOrder(orderData);

      // إنشاء شحنة Bosta إذا كانت طريقة الشحن Bosta
      if (isBostaShipping) {
        try {
          await createOrderBostaDelivery(createdOrder.id);
        } catch (bostaErr) {
          console.error('Bosta delivery creation failed (order still created):', bostaErr);
        }
      }

      // تعيين معرف الطلب ورقمه الواضح ووضع الطلب كمكتمل
      setOrderId(createdOrder.id);
      setOrderNumber(createdOrder.orderNumber || null);
      setOrderComplete(true);

      // حفظ العنوان الجديد إذا كان المستخدم مسجلاً ولم يكن العنوان محفوظاً
      if (currentUser?.id && useNewAddress && shippingData.fullName && shippingData.phone && shippingData.address) {
        try {
          await addAddress(currentUser.id, {
            label: shippingData.city || 'العنوان',
            fullName: shippingData.fullName,
            phone: shippingData.phone,
            email: shippingData.email,
            address: shippingData.address,
            city: isBostaShipping ? (shippingData.bostaCityName || '') : shippingData.city,
            region: isBostaShipping ? (shippingData.bostaDistrictName || '') : shippingData.region,
            building: shippingData.building,
            floor: shippingData.floor,
            apartment: shippingData.apartment,
            bostaCityId: shippingData.bostaCityId,
            bostaCityName: shippingData.bostaCityName,
            bostaZoneId: shippingData.bostaZoneId,
            bostaZoneName: shippingData.bostaZoneName,
            bostaDistrictId: shippingData.bostaDistrictId,
            bostaDistrictName: shippingData.bostaDistrictName,
            defaultShippingMethod: shippingData.shippingMethod,
            defaultPaymentMethod: paymentData.paymentMethod,
            isDefault: savedAddresses.length === 0
          });
        } catch (saveErr) {
          console.error('Error saving address:', saveErr);
        }
      }

      // مسح سلة التسوق
      clearCart();

      // الانتقال إلى الخطوة التالية
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } catch (err) {
      console.error('Error placing order:', err);
      setError('حدث خطأ أثناء إتمام الطلب. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  // عرض نموذج معلومات الشحن
  const renderShippingForm = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        معلومات الشحن
      </Typography>
      <Grid container spacing={3}>
        {/* اختيار العنوان المحفوظ */}
        {savedAddresses.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 1 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                العناوين المحفوظة
              </Typography>
              <Grid container spacing={2}>
                {savedAddresses.map((addr) => (
                  <Grid item xs={12} sm={6} md={4} key={addr.id}>
                    <Paper
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        border: selectedAddressId === addr.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        bgcolor: selectedAddressId === addr.id ? '#e3f2fd' : 'background.paper',
                        '&:hover': { borderColor: '#1976d2' }
                      }}
                      onClick={() => selectAddress(addr)}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {addr.label}
                        {addr.isDefault && (
                          <Chip label="افتراضي" size="small" color="primary" sx={{ ml: 1, fontSize: 10 }} />
                        )}
                      </Typography>
                      <Typography variant="body2">{addr.fullName}</Typography>
                      <Typography variant="body2" color="text.secondary">{addr.phone}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {addr.address}, {addr.city}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
                <Grid item xs={12} sm={6} md={4}>
                  <Paper
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      border: useNewAddress ? '2px solid #4caf50' : '1px dashed #999',
                      bgcolor: useNewAddress ? '#e8f5e9' : 'background.paper',
                      '&:hover': { borderColor: '#4caf50' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 100
                    }}
                    onClick={selectNewAddress}
                  >
                    <Typography variant="body1" color={useNewAddress ? 'success.main' : 'text.secondary'}>
                      + عنوان جديد
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* لا يوجد عناوين محفوظة */}
        {savedAddresses.length === 0 && currentUser && (
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 2 }}>
              يمكنك حفظ هذا العنوان لاستخدامه في المرات القادمة
            </Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            required
            id="fullName"
            name="fullName"
            label="الاسم الكامل"
            fullWidth
            value={shippingData.fullName}
            onChange={handleShippingChange}
            error={!!formErrors.fullName}
            helperText={formErrors.fullName}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            id="phone"
            name="phone"
            label="رقم الهاتف"
            fullWidth
            value={shippingData.phone}
            onChange={handleShippingChange}
            error={!!formErrors.phone}
            helperText={formErrors.phone}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            id="email"
            name="email"
            label="البريد الإلكتروني"
            fullWidth
            value={shippingData.email}
            onChange={handleShippingChange}
            error={!!formErrors.email}
            helperText={formErrors.email}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            id="address"
            name="address"
            label="العنوان"
            fullWidth
            value={shippingData.address}
            onChange={handleShippingChange}
            error={!!formErrors.address}
            helperText={formErrors.address}
          />
        </Grid>
        {shippingData.shippingMethod !== 'bosta' && (
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!formErrors.city} required>
              <InputLabel id="city-dropdown-label">المدينة *</InputLabel>
              <Select
                labelId="city-dropdown-label"
                id="city-dropdown"
                value={shippingData.city}
                label="المدينة *"
                onChange={(e) => handleCityDropdownChange(e.target.value)}
              >
                {getAvailableCities().map((city) => (
                  <MenuItem key={city.id} value={city.name}>
                    {city.name}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.city && (
                <FormHelperText>{formErrors.city}</FormHelperText>
              )}
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            طريقة الشحن *
          </Typography>
          {formErrors.shippingMethod && (
            <Alert severity="error" sx={{ mb: 2 }}>{formErrors.shippingMethod}</Alert>
          )}
          <Grid container spacing={2}>
            {shippingCompanies.map((company) => (
              <Grid item xs={12} sm={6} md={4} key={company.id}>
                <Paper
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    border: shippingData.shippingMethod === company.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    bgcolor: shippingData.shippingMethod === company.id ? '#e3f2fd' : 'background.paper',
                    '&:hover': { borderColor: '#1976d2', boxShadow: 2 },
                    transition: 'all 0.2s'
                  }}
                  onClick={() => handleShippingMethodChange({ target: { value: company.id } } as any)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {company.id === 'pickup' ? (
                      <Typography sx={{ fontSize: 24, mr: 1 }}>🏪</Typography>
                    ) : company.id === 'bosta' ? (
                      <Typography sx={{ fontSize: 24, mr: 1 }}>🚚</Typography>
                    ) : (
                      <Typography sx={{ fontSize: 24, mr: 1 }}>📦</Typography>
                    )}
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {company.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {company.id === 'pickup' ? 'الاستلام من المتجر' : 
                     company.id === 'bosta' ? 'حساب تلقائي حسب الموقع' : 
                     'السعر حسب مدينتك'}
                  </Typography>
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                    {company.id === 'pickup' ? 'مجاني' : 
                     company.id === 'bosta' ? 'حسب الموقع' :
                     'السعر حسب مدينتك'}
                  </Typography>
                  {company.id !== 'pickup' && company.id !== 'bosta' && company.regions?.[0]?.estimatedDeliveryDays && (
                    <Typography variant="caption" color="text.secondary">
                      خلال {company.regions[0].estimatedDeliveryDays} أيام عمل
                    </Typography>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grid>


        {shippingData.shippingMethod === 'bosta' && (
          <>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!formErrors.region}>
                <InputLabel id="bosta-city-label">المدينة *</InputLabel>
                <Select
                  labelId="bosta-city-label"
                  value={shippingData.bostaCityId || ''}
                  label="المدينة *"
                  onChange={(e) => {
                    const cityId = e.target.value;
                    const city = bostaCities.find(c => c._id === cityId);
                    setShippingData(prev => ({
                      ...prev,
                      bostaCityId: cityId,
                      bostaCityName: city?.name || '',
                      bostaZoneId: '',
                      bostaZoneName: '',
                      bostaDistrictId: '',
                      bostaDistrictName: ''
                    }));
                    if (formErrors.region) {
                      setFormErrors(prev => { const n = { ...prev }; delete n.region; return n; });
                    }
                  }}
                  disabled={bostaLoading}
                >
                  {bostaLoading ? (
                    <MenuItem disabled>جاري التحميل...</MenuItem>
                  ) : (
                    bostaCities.map(city => (
                      <MenuItem key={city._id} value={city._id}>{city.name}</MenuItem>
                    ))
                  )}
                </Select>
                <FormHelperText>
                  {bostaLoading ? 'جاري تحميل المدن من Bosta...' : (formErrors.region || '')}
                </FormHelperText>
              </FormControl>
            </Grid>

            {shippingData.bostaCityId && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>المنطقة (Zone)</InputLabel>
                  <Select
                    value={shippingData.bostaZoneId || ''}
                    label="المنطقة (Zone)"
                    onChange={(e) => {
                      const zoneId = e.target.value;
                      const zone = bostaZones.find(z => z._id === zoneId);
                      setShippingData(prev => ({
                        ...prev,
                        bostaZoneId: zoneId,
                        bostaZoneName: zone?.name || ''
                      }));
                    }}
                  >
                    <MenuItem value="">بدون منطقة</MenuItem>
                    {bostaZones.map(zone => (
                      <MenuItem key={zone._id} value={zone._id}>{zone.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {shippingData.bostaCityId && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="الحي / المنطقة"
                  value={shippingData.bostaDistrictName || ''}
                  onChange={(e) => {
                    setShippingData(prev => ({ ...prev, bostaDistrictName: e.target.value }));
                    if (formErrors.region) {
                      setFormErrors(prev => { const n = { ...prev }; delete n.region; return n; });
                    }
                  }}
                  helperText="أدخل اسم الحي أو المنطقة"
                />
              </Grid>
            )}

            {bostaLoadingRate && (
              <Grid item xs={12}>
                <Alert severity="info">جاري حساب تكلفة الشحن...</Alert>
              </Grid>
            )}

            {bostaRate && !bostaLoadingRate && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: '#e3f2fd', border: '1px solid #90caf9' }}>
                  <Typography variant="subtitle1" gutterBottom>
                    تكلفة الشحن من Bosta
                  </Typography>
                  <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                    {bostaRate.total.toLocaleString('ar-EG')} ج.م
                  </Typography>
                  {bostaRate.discount > 0 && (
                    <Typography variant="body2" color="success.main">
                      خصم: {bostaRate.discount.toLocaleString('ar-EG')} ج.م
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    التوصيل خلال {bostaRate.estimatedDeliveryDays} أيام عمل
                  </Typography>
                </Paper>
              </Grid>
            )}

            {bostaRate && !bostaLoadingRate && (
              <Grid item xs={12}>
                <Alert severity="success">
                  سيتم حساب تكلفة الشحن من Bosta تلقائياً ({bostaRate.total.toLocaleString('ar-EG')} ج.م)
                </Alert>
              </Grid>
            )}

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="رقم المبنى"
                name="building"
                value={shippingData.building || ''}
                onChange={handleShippingChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="الدور"
                name="floor"
                value={shippingData.floor || ''}
                onChange={handleShippingChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="الشقة"
                name="apartment"
                value={shippingData.apartment || ''}
                onChange={handleShippingChange}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <TextField
            id="notes"
            name="notes"
            label="ملاحظات"
            fullWidth
            multiline
            rows={4}
            value={shippingData.notes}
            onChange={handleShippingChange}
          />
        </Grid>
      </Grid>
    </Box>
  );

  // عرض نموذج طريقة الدفع
  const renderPaymentForm = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        طريقة الدفع
      </Typography>
      <FormControl component="fieldset">
        <RadioGroup
          name="paymentMethod"
          value={paymentData.paymentMethod}
          onChange={handlePaymentMethodChange}
        >
          <FormControlLabel
            value={PaymentMethod.CASH_ON_DELIVERY}
            control={<Radio />}
            disabled={!availablePaymentMethods.includes(PaymentMethod.CASH_ON_DELIVERY)}
            label="الدفع عند الاستلام"
          />
          <FormControlLabel
            value={PaymentMethod.BANK_TRANSFER}
            control={<Radio />}
            disabled={!availablePaymentMethods.includes(PaymentMethod.BANK_TRANSFER)}
            label="تحويل بنكي"
          />
          <FormControlLabel
            value={PaymentMethod.ELECTRONIC_PAYMENT}
            control={<Radio />}
            disabled={!availablePaymentMethods.includes(PaymentMethod.ELECTRONIC_PAYMENT)}
            label="دفع إلكتروني"
          />
        </RadioGroup>
        {availablePaymentMethods.length === 0 && (
          <FormHelperText error>
            لا توجد طرق دفع مفعلة حالياً. يرجى التواصل مع إدارة المتجر.
          </FormHelperText>
        )}
      </FormControl>

      {paymentData.paymentMethod === PaymentMethod.BANK_TRANSFER && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="subtitle1" gutterBottom>
            تفاصيل التحويل البنكي:
          </Typography>
          <Typography variant="body2">
            اسم البنك: بنك مصر
          </Typography>
          <Typography variant="body2">
            اسم الحساب: مركز صيانة التقنية
          </Typography>
          <Typography variant="body2">
            رقم الحساب: 1234567890
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            يرجى إرسال صورة من إيصال التحويل على رقم الواتساب: 01234567890
          </Typography>
        </Box>
      )}

      {paymentData.paymentMethod === PaymentMethod.ELECTRONIC_PAYMENT && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="subtitle1" gutterBottom>
            سيتم تفعيل الدفع الإلكتروني قريباً
          </Typography>
          <Typography variant="body2">
            يمكنك حالياً استخدام الدفع عند الاستلام أو التحويل البنكي
          </Typography>
        </Box>
      )}
    </Box>
  );

  // عرض ملخص الطلب
  const renderOrderSummary = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        ملخص الطلب
      </Typography>

      <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
        {cart.items.map((item) => (
          <ListItem key={item.productId} alignItems="flex-start">
            <ListItemAvatar>
              <Avatar
                src={item.product.imageUrl || placeholderImage}
                alt={item.product.name}
                variant="rounded"
                sx={{ width: 60, height: 60, mr: 2 }}
              />
            </ListItemAvatar>
            <ListItemText
              primary={item.product.name}
              secondary={
                <React.Fragment>
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.primary"
                  >
                    {item.product.sellingPrice.toLocaleString('ar-EG')} ج.م × {item.quantity}
                  </Typography>
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.primary"
                    sx={{ display: 'block', fontWeight: 'bold' }}
                  >
                    {(item.product.sellingPrice * item.quantity).toLocaleString('ar-EG')} ج.م
                  </Typography>
                </React.Fragment>
              }
            />
          </ListItem>
        ))}
      </List>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          معلومات الشحن:
        </Typography>
        <Typography variant="body2">
          {shippingData.fullName}
        </Typography>
        {shippingData.shippingMethod === 'pickup' ? (
          <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
            🏪 الاستلام من المتجر - مجاني
          </Typography>
        ) : shippingData.shippingMethod === 'bosta' ? (
          <>
            <Typography variant="body2">
              {shippingData.address}
              {shippingData.building && ` - مبنى ${shippingData.building}`}
              {shippingData.floor && ` - دور ${shippingData.floor}`}
              {shippingData.apartment && ` - شقة ${shippingData.apartment}`}
            </Typography>
          </>
        ) : (
          <Typography variant="body2">
            {shippingData.address}, {shippingData.city}
          </Typography>
        )}
        <Typography variant="body2">
          هاتف: {shippingData.phone}
        </Typography>
        <Typography variant="body2">
          البريد الإلكتروني: {shippingData.email}
        </Typography>
        <Typography variant="body2">
          طريقة الشحن: {shippingCompanies.find(c => c.id === shippingData.shippingMethod)?.name}
        </Typography>
        {shippingData.shippingMethod === 'bosta' && shippingData.bostaCityName && (
          <Typography variant="body2">
            المدينة: {shippingData.bostaCityName}
            {shippingData.bostaDistrictName && ` - ${shippingData.bostaDistrictName}`}
          </Typography>
        )}
        {selectedRegion && shippingData.shippingMethod !== 'bosta' && shippingData.shippingMethod !== 'pickup' && (
          <Typography variant="body2">
            المنطقة: {selectedRegion.name}
          </Typography>
        )}
        {shippingData.notes && (
          <Typography variant="body2">
            ملاحظات: {shippingData.notes}
          </Typography>
        )}
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          طريقة الدفع:
        </Typography>
        <Typography variant="body2">
          {paymentData.paymentMethod === PaymentMethod.CASH_ON_DELIVERY && 'الدفع عند الاستلام'}
          {paymentData.paymentMethod === PaymentMethod.BANK_TRANSFER && 'تحويل بنكي'}
          {paymentData.paymentMethod === PaymentMethod.ELECTRONIC_PAYMENT && 'دفع إلكتروني'}
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle1">المجموع الفرعي:</Typography>
        <Typography variant="subtitle1">
          {cart.totalAmount.toLocaleString('ar-EG')} ج.م
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle1">تكلفة الشحن:</Typography>
        <Typography variant="subtitle1">
          {shippingData.shippingMethod === 'pickup' ? 'مجاني' :
           shippingData.shippingMethod === 'bosta'
            ? `${(bostaRate?.total || 0).toLocaleString('ar-EG')} ج.م`
            : `${(selectedRegion?.cost || 0).toLocaleString('ar-EG')} ج.م`
          }
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">الإجمالي:</Typography>
        <Typography variant="h6" fontWeight="bold">
          {(cart.totalAmount + (shippingData.shippingMethod === 'pickup' ? 0 : 
            shippingData.shippingMethod === 'bosta' ? (bostaRate?.total || 0) : (selectedRegion?.cost || 0)
          )).toLocaleString('ar-EG')} ج.م
        </Typography>
      </Box>
    </Box>
  );

  // عرض تأكيد الطلب
  const renderOrderConfirmation = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <Typography variant="h5" color="primary" gutterBottom>
        🎉 تم تأكيد طلبك بنجاح!
      </Typography>

      {/* رقم الطلب الواضح للإرسال للاستفسار */}
      <Paper
        elevation={2}
        sx={{
          display: 'inline-block',
          px: 4,
          py: 2,
          mb: 3,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          borderRadius: 2
        }}
      >
        <Typography variant="caption" sx={{ opacity: 0.85, display: 'block' }}>
          رقم طلبك
        </Typography>
        <Typography variant="h5" fontWeight="bold" letterSpacing={2}>
          {orderNumber || orderId?.substring(0, 8).toUpperCase()}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.75, display: 'block', mt: 0.5 }}>
          احتفظ بهذا الرقم للاستفسار عن طلبك
        </Typography>
      </Paper>

      <Typography variant="body1" paragraph>
        سيتم التواصل معك قريباً لتأكيد الطلب وتحديد موعد التوصيل.
      </Typography>

      <Typography variant="body1" paragraph>
        يمكنك متابعة حالة طلبك من خلال صفحة "طلباتي" في حسابك.
      </Typography>

      {shippingData.shippingMethod === 'bosta' && (
        <Typography variant="body1" paragraph color="primary">
          تم إرسال طلب الشحن إلى Bosta. يمكنك تتبع شحنتك من خلال
          <Link component={RouterLink} to="/tracking" sx={{ mr: 0.5, ml: 0.5 }}>
            صفحة تتبع الشحنات
          </Link>
        </Typography>
      )}

      <Box sx={{ mt: 4 }}>
        <Button
          variant="contained"
          color="primary"
          component={RouterLink}
          to="/"
          sx={{ mr: 2 }}
        >
          العودة للمتجر
        </Button>

        <Button
          variant="outlined"
          component={RouterLink}
          to={`/order/${orderId}`}
        >
          تفاصيل الطلب
        </Button>
      </Box>
    </Box>
  );

  // عرض المحتوى حسب الخطوة الحالية
  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderShippingForm();
      case 1:
        return renderPaymentForm();
      case 2:
        return renderOrderSummary();
      case 3:
        return renderOrderConfirmation();
      default:
        return 'خطوة غير معروفة';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            component={RouterLink}
            to="/"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            الرئيسية
          </Link>
          <Link
            component={RouterLink}
            to="/"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <StoreIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            المتجر
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <ShoppingCartIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            إتمام الطلب
          </Typography>
        </Breadcrumbs>
      </Box>

      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Stepper activeStep={activeStep} sx={{ pt: 3, pb: 5 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {getStepContent(activeStep)}

        {activeStep <= 2 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            {activeStep !== 0 && (
              <Button
                onClick={handleBack}
                sx={{ mr: 1 }}
                disabled={loading || (activeStep === 1 && availablePaymentMethods.length === 0)}
              >
                رجوع
              </Button>
            )}

            {activeStep === 2 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handlePlaceOrder}
                disabled={loading || cart.items.length === 0}
              >
                {loading ? <CircularProgress size={24} /> : 'تأكيد الطلب'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loading}
              >
                التالي
              </Button>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default CheckoutPage;
